import { useState } from 'react';
import { ExternalLink } from 'lucide-react';
import { format } from 'date-fns';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { AtsBadge, ScoutTaskStatusBadge } from '@/components/scout/ScoutStatusBadge';
import { useScoutRunTasks } from '@/hooks/useScout';
import type { ScoutRunSummary, ScoutRunTaskDetail, ScoutTaskStatus } from '@repo/types';

/**
 * What happened to each site in a run.
 *
 * The runs table can only say "48/48 sites · 2 failed", which tells an operator
 * that something broke but not what or where — and the failures are the
 * actionable part of a run. A site that failed is one to deactivate, to flag
 * `requires_search`, or to look at because it has moved; none of that is
 * possible without its name and its error.
 *
 * Opens on the failures for that reason, with the full list one click away.
 */
export function ScoutRunSheet({
  run,
  onOpenChange,
}: {
  run: ScoutRunSummary | null;
  onOpenChange: (open: boolean) => void;
}) {
  // Reset per run: a sheet opened from a "2 failed" cell should always land on
  // those two, even if the last one was left showing every site.
  const [showAll, setShowAll] = useState(false);

  return (
    <Sheet
      open={Boolean(run)}
      onOpenChange={(open) => {
        if (!open) setShowAll(false);
        onOpenChange(open);
      }}
    >
      <SheetContent className="w-full overflow-y-auto sm:max-w-2xl">
        {run && <RunTasks run={run} showAll={showAll} onShowAllChange={setShowAll} />}
      </SheetContent>
    </Sheet>
  );
}

function RunTasks({
  run,
  showAll,
  onShowAllChange,
}: {
  run: ScoutRunSummary;
  showAll: boolean;
  onShowAllChange: (value: boolean) => void;
}) {
  const status: ScoutTaskStatus | undefined = showAll ? undefined : 'failed';
  const { data, isLoading } = useScoutRunTasks(run.id, status);
  const tasks = data?.items ?? [];
  const counts = run.taskCounts;
  const total = counts.pending + counts.running + counts.done + counts.failed + counts.cancelled;

  return (
    <>
      <SheetHeader className="space-y-1">
        <SheetTitle>Run · {format(new Date(run.startedAt), 'd MMM yyyy, HH:mm')}</SheetTitle>
        <SheetDescription>
          {total} site{total === 1 ? '' : 's'} · {counts.done} done
          {counts.failed > 0 && ` · ${counts.failed} failed`}
          {run.roleFilter ? ` · role: ${run.roleFilter}` : ''}
          {run.locations.length ? ` · in: ${run.locations.join(', ')}` : ''}
        </SheetDescription>
      </SheetHeader>

      <div className="space-y-4 px-4 pb-6">
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant={showAll ? 'outline' : 'default'}
            onClick={() => onShowAllChange(false)}
          >
            Failed{counts.failed > 0 ? ` (${counts.failed})` : ''}
          </Button>
          <Button
            size="sm"
            variant={showAll ? 'default' : 'outline'}
            onClick={() => onShowAllChange(true)}
          >
            All sites{total > 0 ? ` (${total})` : ''}
          </Button>
        </div>

        {isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        ) : tasks.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            {showAll ? 'This run has no sites.' : 'No site failed in this run.'}
          </p>
        ) : (
          <ul className="space-y-2">
            {tasks.map((task) => (
              <TaskRow key={task.id} task={task} />
            ))}
          </ul>
        )}
      </div>
    </>
  );
}

function TaskRow({ task }: { task: ScoutRunTaskDetail }) {
  return (
    <li className="space-y-2 rounded-lg border p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <div className="flex items-center gap-2">
            {/* Empty for targets whose URL names no employer — the Oracle
                tenants. The link below is the identification. */}
            <span className="truncate font-medium">
              {task.company || <span className="italic text-muted-foreground">Unnamed</span>}
            </span>
            <AtsBadge ats={task.ats} />
          </div>
          <a
            href={task.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex max-w-[380px] items-center gap-1 truncate text-xs text-muted-foreground hover:underline"
            title={task.url}
          >
            <span className="truncate">{task.url}</span>
            <ExternalLink className="size-3 shrink-0" />
          </a>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {task.status === 'done' && (
            <Badge variant="outline" className="font-mono text-[10px]">
              {task.found} kept
            </Badge>
          )}
          <ScoutTaskStatusBadge status={task.status} />
        </div>
      </div>

      {/* Verbatim, because these strings are the diagnosis: "The agent did not
          finish in time." and "The site navigated away 4 times" call for
          entirely different fixes. */}
      {task.error && (
        <p className="rounded bg-rose-50 px-2 py-1.5 font-mono text-xs text-rose-700 dark:bg-rose-950/40 dark:text-rose-300">
          {task.error}
        </p>
      )}

      {task.status === 'done' && (task.skipped > 0 || task.duplicates > 0) && (
        <p className="text-xs text-muted-foreground">
          {task.skipped > 0 && `${task.skipped} unusable`}
          {task.skipped > 0 && task.duplicates > 0 && ' · '}
          {task.duplicates > 0 && `${task.duplicates} duplicate`}
        </p>
      )}
    </li>
  );
}
